/**
 * RetailRoomPlanner — 2D drag & drop room editor, wired to the 3D viewer.
 *
 * Left  : catalogue of ioi items, filtered by family, with their real price.
 * Middle: the room, in SVG, 1 cm = 1 unit, snapped to the voxel grid.
 * Right : metrics, collisions, and the buttons that push the layout into ioi.
 */

/* global frappe, RetailRoom3D */

class RetailRoomPlanner {
	constructor(container, options = {}) {
		this.root = typeof container === "string" ? document.querySelector(container) : container;
		this.options = Object.assign({ grid: 10, onChange: null }, options);
		this.state = {
			plan: null,
			title: "Nouveau plan",
			customer: null,
			journal: null,
			width: 400,
			depth: 300,
			height: 250,
			items: [],
			catalog: [],
			family: null,
			selected: null,
			view: "2d",
			metrics: null,
			objectsOpen: true,
			eyeHeight: 175,
			materials: null,
		};
		this.viewer = null;
	}

	async mount() {
		this.root.innerHTML = TEMPLATE;
		this.el = {
			catalog: this.root.querySelector("#rp-catalog"),
			families: this.root.querySelector("#rp-families"),
			stage: this.root.querySelector("#rp-stage"),
			stage3d: this.root.querySelector("#rp-stage3d"),
			metrics: this.root.querySelector("#rp-metrics"),
			warnings: this.root.querySelector("#rp-warnings"),
			title: this.root.querySelector("#rp-title"),
			width: this.root.querySelector("#rp-width"),
			depth: this.root.querySelector("#rp-depth"),
			customer: this.root.querySelector("#rp-customer"),
			journal: this.root.querySelector("#rp-journal"),
			search: this.root.querySelector("#rp-search"),
			inspector: this.root.querySelector("#rp-inspector"),
			objects: this.root.querySelector("#rp-objects"),
			eye: this.root.querySelector("#rp-eye"),
		};
		this.viewer = new RetailRoom3D(this.el.stage3d, {
			mode: "iso",
			editable: true,
			onSelect: (box) => box && this.selectItem(box.id),
			// dragging in the 3D view writes straight into the plan, so the 2D
			// view and the metrics stay in step without a round trip
			onMove: (id, delta) => this.nudgeById(id, delta),
			onMoveEnd: () => this.commitMove(),
			onContext: (box, at) => box && this.openMaterialPicker(box, at),
		});
		this._bind();
		this._bindFullscreenPanel();
		this.shapes = await frappe.xcall("ioi_retail_suite.api.shape_catalog", {});
		this.state.materials = await frappe.xcall("ioi_retail_suite.api.material_catalog", {});
		// one texture tile is a different size on oak and on a fabric weave ;
		// the server owns that table, the renderer only borrows it
		this.viewer.options.materialTiles = tileTable(this.state.materials);
		await this.loadCatalog();
		await this.loadCustomers();
		if (this.options.plan) await this.loadPlan(this.options.plan);
		else this.draw();
		return this;
	}

	// ------------------------------------------------------------- loading
	async loadCatalog(search) {
		const rows = await frappe.xcall("ioi_retail_suite.api.planner_catalog", {
			search: search || "",
			families: this.state.family || "",
			limit: 140,
		});
		this.state.catalog = rows || [];
		this.renderCatalog();
	}

	async loadCustomers() {
		const rows = await frappe.xcall("ioi_retail_suite.api.get_customers", { limit: 200 });
		this.el.customer.innerHTML =
			`<option value="">— client —</option>` +
			(rows || [])
				.map((r) => `<option value="${r.name}">${frappe.utils.escape_html(r.full_name || r.name)}</option>`)
				.join("");
		const model = await frappe.xcall("ioi_retail_suite.api.get_models", {});
		const first = (model || [])[0];
		if (first) {
			const payload = await frappe.xcall("ioi_retail_suite.api.get_model", { model: first.name });
			this.state.model = first.name;
			this.el.journal.innerHTML = (payload.journals || [])
				.map((j) => `<option value="${j.journal}">${frappe.utils.escape_html(j.label)}</option>`)
				.join("");
		}
	}

	/**
	 * The saved plans, as a wall of real 2D previews.
	 *
	 * The thumbnails are drawn server-side by the same routine as the PDF, so
	 * what the card shows is the plan itself and not a stale screenshot.
	 */
	async openPlanDialog() {
		const rows = await frappe.xcall("ioi_retail_suite.api.plan_previews", { limit: 24 });
		if (!rows || !rows.length) {
			frappe.msgprint("Aucun plan enregistré pour le moment.");
			return;
		}
		const dialog = new frappe.ui.Dialog({
			title: "Ouvrir un plan",
			size: "extra-large",
			fields: [{ fieldtype: "HTML", fieldname: "gallery" }],
		});
		const cards = rows
			.map(
				(r) => `
			<button class="rp-plancard${r.plan === this.state.plan ? " on" : ""}" data-plan="${r.plan}">
				<div class="rp-planshot">${r.preview}</div>
				<div class="rp-planmeta">
					<b>${frappe.utils.escape_html(r.title)}</b>
					<span>${r.width_cm}×${r.depth_cm} cm · ${r.pieces} meuble${r.pieces > 1 ? "s" : ""}</span>
					<span>${format_currency(r.total_value)} · circulation ${r.circulation_pct} %</span>
					<span class="rp-planwhen">${frappe.datetime.comment_when(r.modified)}</span>
				</div>
			</button>`
			)
			.join("");
		dialog.fields_dict.gallery.$wrapper.html(`<div class="rp-plangrid">${cards}</div>`);
		dialog.fields_dict.gallery.$wrapper.find("[data-plan]").each((_, node) => {
			node.onclick = () => {
				dialog.hide();
				this.loadPlan(node.dataset.plan);
			};
		});
		dialog.show();
	}

	/** Reopen a saved plan : header, furniture, then redraw. */
	async loadPlan(name) {
		if (!name) return;
		const payload = await frappe.xcall("ioi_retail_suite.api.load_plan", { plan: name });
		this.state.plan = payload.plan;
		this.state.title = payload.title;
		this.state.width = payload.width_cm;
		this.state.depth = payload.depth_cm;
		this.state.height = payload.wall_height_cm;
		this.state.items = payload.items || [];
		this.state.selected = null;
		this.el.title.value = payload.title || "";
		this.el.width.value = payload.width_cm;
		this.el.depth.value = payload.depth_cm;
		if (payload.customer) this.setCustomer(payload.customer);
		this.state.height = payload.wall_height_cm || 250;
		this.draw();
		frappe.show_alert({
			message: `Plan ${payload.plan} ouvert — ${this.state.items.length} meubles`,
			indicator: "blue",
		});
		return payload;
	}

	newPlan() {
		this.state.plan = null;
		this.state.items = [];
		this.state.selected = null;
		this.el.title.value = "Nouveau plan";
		this.draw();
	}

	// ------------------------------------------------------------ rendering
	renderCatalog() {
		const families = [...new Set(this.state.catalog.map((c) => c.family).filter(Boolean))];
		this.el.families.innerHTML =
			`<button class="rp-fam${!this.state.family ? " on" : ""}" data-family="">Tout</button>` +
			families
				.map(
					(f) =>
						`<button class="rp-fam${this.state.family === f ? " on" : ""}" data-family="${f}">${f.replace("IK-", "")}</button>`
				)
				.join("");

		this.el.catalog.innerHTML = this.state.catalog
			.map(
				(row) => `
			<div class="rp-card" draggable="true" data-item="${row.item}">
				${row.image ? `<img src="${row.image}" alt="">` : `<div class="rp-noimg"></div>`}
				<div class="rp-card-b">
					<b>${frappe.utils.escape_html(row.label)}</b>
					<span>${row.width_cm}×${row.depth_cm} cm · ${format_currency(row.price)}</span>
				</div>
			</div>`
			)
			.join("");

		this.el.families.querySelectorAll("[data-family]").forEach((node) => {
			node.onclick = () => {
				this.state.family = node.dataset.family || null;
				this.loadCatalog(this.el.search.value);
			};
		});
		this.el.catalog.querySelectorAll(".rp-card").forEach((node) => {
			node.addEventListener("dragstart", (event) => {
				event.dataTransfer.setData("text/plain", node.dataset.item);
			});
			node.addEventListener("dblclick", () => this.addItem(node.dataset.item));
		});
	}

	draw() {
		if (this.state.view === "2d") {
			this.el.stage.style.display = "";
			this.el.stage3d.style.display = "none";
			this.draw2d();
		} else {
			this.el.stage.style.display = "none";
			this.el.stage3d.style.display = "";
			this.draw3d();
		}
		this.renderObjectList();
		this.renderInspector();
		this.refreshMetrics();
	}

	draw2d() {
		const { width, depth, items, selected } = this.state;
		const pad = 30;
		const host = this.el.stage;
		const scale = Math.min(
			(host.clientWidth - pad * 2) / width,
			(host.clientHeight - pad * 2) / depth
		);
		const gx = this.options.grid * scale;

		const shapes = items
			.map((row, index) => {
				// x_cm / y_cm anchor the bounding box ; the piece turns inside it
				const [w, d] = footprint(row);
				const on = selected === index;
				const pw = w * scale;
				const ph = d * scale;
				const bw = (Number(row.width_cm) || 80) * scale;
				const bh = (Number(row.depth_cm) || 60) * scale;
				const stroke = row.color || "#0058a3";
				const spin = angleOf(row);
				const inner = this.outlineSVG(row, pw, ph, bw, bh, spin, stroke);
				const raised = Number(row.z_cm) > 0
					? `<g transform="translate(${pw / 2} ${ph / 2})">
							<circle r="10" fill="#fff" stroke="${stroke}" stroke-width="1.2"/>
							<text class="rp-zbadge" y="3.5">${Math.round(row.z_cm)}</text>
						</g>`
					: "";
				// the bounding box is only worth showing once the piece is turned
				const bounds = spin % 90
					? `<rect width="${pw}" height="${ph}" fill="none" stroke="${stroke}"
							stroke-width="0.8" stroke-dasharray="4 3" opacity="0.5" rx="2"/>`
					: "";
				return `<g class="rp-shape${on ? " on" : ""}" data-index="${index}"
					transform="translate(${pad + row.x_cm * scale} ${pad + row.y_cm * scale})">
					${bounds}
					${inner}
					<rect x="${(pw - bw) / 2}" y="${(ph - bh) / 2}" width="${bw}" height="${bh}"
						fill="none" stroke="${stroke}" stroke-width="${on ? 2.5 : 1.2}" rx="2"
						transform="rotate(${spin} ${pw / 2} ${ph / 2})"/>
					${raised}
					<text x="6" y="15">${frappe.utils.escape_html((row.label || row.item).slice(0, 22))}</text>
				</g>`;
			})
			.join("");

		host.innerHTML = `<svg width="100%" height="100%">
			<defs><pattern id="rpGrid" width="${gx}" height="${gx}" patternUnits="userSpaceOnUse">
				<path d="M ${gx} 0 L 0 0 0 ${gx}" fill="none" stroke="#dfe3e8" stroke-width="0.6"/>
			</pattern></defs>
			<rect x="${pad}" y="${pad}" width="${width * scale}" height="${depth * scale}"
				fill="url(#rpGrid)" stroke="#0f1419" stroke-width="2"/>
			<text class="rp-dim" x="${pad + (width * scale) / 2}" y="${pad - 10}">${width} cm</text>
			<text class="rp-dim" x="${pad - 12}" y="${pad + (depth * scale) / 2}"
				transform="rotate(-90 ${pad - 12} ${pad + (depth * scale) / 2})">${depth} cm</text>
			${shapes}
		</svg>`;

		host.ondragover = (event) => event.preventDefault();
		host.ondrop = (event) => {
			event.preventDefault();
			const item = event.dataTransfer.getData("text/plain");
			const rect = host.getBoundingClientRect();
			this.addItem(item, {
				x: Math.max(0, Math.round((event.clientX - rect.left - pad) / scale / this.options.grid) * this.options.grid),
				y: Math.max(0, Math.round((event.clientY - rect.top - pad) / scale / this.options.grid) * this.options.grid),
			});
		};

		host.querySelectorAll(".rp-shape").forEach((node) => {
			const index = Number(node.dataset.index);
			node.addEventListener("pointerdown", (event) => {
				this.state.selected = index;
				const start = { x: event.clientX, y: event.clientY };
				const origin = { x: this.state.items[index].x_cm, y: this.state.items[index].y_cm };
				const move = (moveEvent) => {
					const g = this.options.grid;
					this.state.items[index].x_cm = Math.max(
						0, Math.round((origin.x + (moveEvent.clientX - start.x) / scale) / g) * g);
					this.state.items[index].y_cm = Math.max(
						0, Math.round((origin.y + (moveEvent.clientY - start.y) / scale) / g) * g);
					this.draw2d();
				};
				const up = () => {
					window.removeEventListener("pointermove", move);
					window.removeEventListener("pointerup", up);
					this.draw();
				};
				window.addEventListener("pointermove", move);
				window.addEventListener("pointerup", up);
			});
		});
	}

	async draw3d() {
		const scene = await frappe.xcall("ioi_retail_suite.api.scene", {
			items: JSON.stringify(this.state.items),
			width_cm: this.state.width,
			depth_cm: this.state.depth,
			wall_height_cm: this.state.height,
			grid: this.options.grid,
		});
		this.viewer.setScene(scene);
	}

	/**
	 * Draw one archetype at its real size, centred in the bounding box.
	 *
	 * The silhouette is authored for the unturned piece, so it is drawn in the
	 * piece's own width x depth and then spun around the centre of the box —
	 * which is exactly the pivot the 3D renderer uses.
	 */
	outlineSVG(row, w, h, bw, bh, spin, stroke) {
		const table = (this.shapes && this.shapes.outlines) || {};
		const prims = table[row.archetype] || (this.shapes && this.shapes.fallback) || [];
		const tx = (w - bw) / 2;
		const ty = (h - bh) / 2;

		const body = prims
			.map((prim) => {
				const x = prim.x * bw;
				const y = prim.y * bh;
				const pw = Math.max(1, prim.w * bw);
				const ph = Math.max(1, prim.h * bh);
				const fill = `${stroke}${alphaHex(prim.fill)}`;
				const dash = prim.dash ? ` stroke-dasharray="4 3"` : "";
				if (prim.k === "ellipse") {
					return `<ellipse cx="${x + pw / 2}" cy="${y + ph / 2}" rx="${pw / 2}" ry="${ph / 2}"
						fill="${fill}" stroke="${stroke}" stroke-width="1"${dash}/>`;
				}
				if (prim.k === "line") {
					return `<line x1="${x}" y1="${y}" x2="${x + pw}" y2="${y + ph}"
						stroke="${stroke}" stroke-width="1"${dash}/>`;
				}
				const r = (prim.r || 0) * Math.min(bw, bh);
				return `<rect x="${x}" y="${y}" width="${pw}" height="${ph}" rx="${r}"
					fill="${fill}" stroke="${stroke}" stroke-width="0.8"${dash}/>`;
			})
			.join("");
		return `<g transform="rotate(${spin} ${w / 2} ${h / 2}) translate(${tx} ${ty})">${body}</g>`;
	}

	/** Centre of a placed piece, in world centimetres — what the camera aims at. */
	centreOf(index) {
		const row = this.state.items[index];
		if (!row) return null;
		const [w, d] = footprint(row);
		const h = Number(row.height_cm) || 75;
		return [
			Number(row.x_cm) + w / 2,
			Number(row.y_cm) + d / 2,
			Number(row.z_cm || 0) + h / 2,
		];
	}

	/**
	 * The furniture already in the room, listed on the left.
	 *
	 * In the walkthrough a piece can be behind you, or behind a wardrobe — you
	 * cannot click what you cannot see. Picking it from the list selects it and
	 * turns the camera towards it, so the inspector on the right is always
	 * acting on something visible.
	 */
	renderObjectList() {
		const items = this.state.items || [];
		const header = (count, open) =>
			`<button class="rp-objects-h" id="rp-objfold">
				<span class="rp-caret">${open ? "▾" : "▸"}</span>
				Meubles posés<b>${count}</b>
			</button>`;
		const fold = () => {
			const node = this.el.objects.querySelector("#rp-objfold");
			if (node) {
				node.onclick = () => {
					this.state.objectsOpen = !this.state.objectsOpen;
					this.renderObjectList();
				};
			}
		};

		if (!items.length) {
			// one thin line, not a box : the catalogue below is what is wanted here
			this.el.objects.innerHTML =
				header(0, false) +
				`<div class="rp-objects-empty">Glissez un article sur le plan.</div>`;
			fold();
			return;
		}
		if (!this.state.objectsOpen) {
			this.el.objects.innerHTML = header(items.length, false);
			fold();
			return;
		}
		const rows = items
			.map((row, index) => {
				const on = this.state.selected === index;
				const angle = Math.round(((angleOf(row) % 360) + 360) % 360);
				const marks = [
					`${Math.round(row.x_cm)}·${Math.round(row.y_cm)}`,
					Number(row.z_cm) ? `↑${Math.round(row.z_cm)}` : "",
					angle ? `${angle}°` : "",
					Number(row.rot_x) || Number(row.rot_y) ? "incliné" : "",
				].filter(Boolean);
				return `<div class="rp-obj${on ? " on" : ""}" data-index="${index}">
					<i style="background:${row.color || "#0058a3"}"></i>
					<span class="rp-obj-t">
						<b>${frappe.utils.escape_html((row.label || row.item).slice(0, 28))}</b>
						<em>${marks.join(" · ")}</em>
					</span>
					<button class="rp-obj-go" data-goto="${index}" title="S\u2019y rendre">⤢</button>
				</div>`;
			})
			.join("");
		this.el.objects.innerHTML = header(items.length, true) + `<div class="rp-objlist">${rows}</div>`;
		fold();

		this.el.objects.querySelectorAll("[data-index]").forEach((node) => {
			node.onclick = (event) => {
				if (event.target.closest("[data-goto]")) return;
				this.focusItem(Number(node.dataset.index), false);
			};
		});
		this.el.objects.querySelectorAll("[data-goto]").forEach((node) => {
			node.onclick = (event) => {
				event.stopPropagation();
				this.focusItem(Number(node.dataset.goto), true);
			};
		});
	}

	/** Select a piece, and in the walkthrough put the camera on it. */
	focusItem(index, approach) {
		this.state.selected = index;
		const centre = this.centreOf(index);
		if (centre && this.state.view === "3d" && this.viewer.options.mode === "eye") {
			if (approach) this.viewer.walkTo(centre);
			else this.viewer.lookAt(centre);
		}
		this.viewer.selected = `box-${index}`;
		this.draw();
	}

	/**
	 * Full screen hides the two side columns, so the list and the inspector
	 * move inside the viewer rather than disappearing with them.
	 */
	_bindFullscreenPanel() {
		const home = {
			objects: this.el.objects.parentNode,
			inspector: this.el.inspector.parentNode,
		};
		document.addEventListener("fullscreenchange", () => {
			const full = document.fullscreenElement === this.el.stage3d;
			if (full && !this._fspanel) {
				const panel = document.createElement("div");
				panel.className = "rp-fspanel";
				panel.appendChild(this.el.objects);
				panel.appendChild(this.el.inspector);
				this._fspanel = panel;
				this.viewer.setOverlay(panel);
			} else if (!full && this._fspanel) {
				home.objects.insertBefore(this.el.objects, home.objects.firstChild);
				home.inspector.insertBefore(this.el.inspector, home.inspector.firstChild);
				this.viewer.setOverlay(null);
				this._fspanel.remove();
				this._fspanel = null;
			}
		});
	}

	/**
	 * The inspector : six axes, a material and a height, on the selected piece.
	 *
	 * Numbers rather than 3D handles — a handle the size of a thumbnail cannot
	 * place a wardrobe at half a centimetre, and a sales rep needs the exact
	 * figure to put on the quote anyway.
	 */
	renderInspector() {
		const row = this.state.items[this.state.selected];
		if (!row) {
			this.el.inspector.innerHTML =
				`<div class="rp-inspect-empty">Sélectionnez un meuble pour le déplacer,
				 le pivoter ou changer sa matière.</div>`;
			return;
		}
		const axis = (key, label, step, min) =>
			`<label class="rp-ax"><span>${label}</span>
				<input type="number" data-field="${key}" step="${step}"
					${min !== undefined ? `min="${min}"` : ""}
					value="${Math.round((Number(row[key]) || 0) * 10) / 10}"></label>`;
		const materials = this.state.materials ? this.state.materials.materials || {} : {};
		const current = row.material && materials[row.material]
			? materials[row.material].label
			: "Par défaut";
		this.el.inspector.innerHTML = `
			<div class="rp-inspect">
				<div class="rp-inspect-h">${frappe.utils.escape_html(row.label || row.item)}</div>
				<div class="rp-axgroup"><b>Translation (cm)</b>
					<div class="rp-axrow">${axis("x_cm", "X", 5, 0)}${axis("y_cm", "Y", 5, 0)}${axis("z_cm", "Z", 5, 0)}</div>
				</div>
				<div class="rp-axgroup"><b>Rotation (°)</b>
					<div class="rp-axrow">${axis("rot_x", "X", 15)}${axis("rot_y", "Y", 15)}${axis("rot_z", "Z", 15)}</div>
					<input type="range" class="rp-axslider" data-field="rot_z" min="0" max="359" step="1"
						value="${Math.round((((Number(row.rot_z) || 0) % 360) + 360) % 360)}">
				</div>
				<div class="rp-axgroup"><b>Matière</b>
					<button class="rp-btn rp-block" id="rp-mat">${frappe.utils.escape_html(current)}</button>
				</div>
				<div class="rp-axrow rp-axbtns">
					<button class="rp-btn" data-nudge="-15">−15°</button>
					<button class="rp-btn" data-nudge="15">+15°</button>
					<button class="rp-btn" data-reset="1">Remettre à plat</button>
				</div>
			</div>`;

		this.el.inspector.querySelectorAll("[data-field]").forEach((node) => {
			const handler = () => {
				const patch = {};
				patch[node.dataset.field] = Number(node.value) || 0;
				this.patchSelected(patch);
			};
			node.onchange = handler;
			if (node.type === "range") node.oninput = frappe.utils.debounce(handler, 60);
		});
		this.el.inspector.querySelectorAll("[data-nudge]").forEach((node) => {
			node.onclick = () => this.rotateSelected(Number(node.dataset.nudge));
		});
		const reset = this.el.inspector.querySelector("[data-reset]");
		if (reset) reset.onclick = () => this.patchSelected({ rot_x: 0, rot_y: 0, z_cm: 0 });
		const mat = this.el.inspector.querySelector("#rp-mat");
		if (mat) {
			mat.onclick = (event) => {
				const rect = event.currentTarget.getBoundingClientRect();
				this.openMaterialPicker(
					{ parent: `box-${this.state.selected}` },
					{ x: rect.left + rect.width / 2, y: rect.bottom }
				);
			};
		}
	}

	// -------------------------------------------------------------- actions
	addItem(item, position) {
		const meta = this.state.catalog.find((c) => c.item === item) || {};
		const row = {
			item,
			label: meta.label || item,
			x_cm: position ? position.x : 0,
			y_cm: position ? position.y : 0,
			width_cm: meta.width_cm || 80,
			depth_cm: meta.depth_cm || 60,
			height_cm: meta.height_cm || null,
			archetype: meta.archetype || "BOX",
			color: meta.color || null,
			z_cm: 0,
			rot_x: 0,
			rot_y: 0,
			rot_z: 0,
			material: null,
			rotation: "0",
			qty: 1,
		};
		this.state.items.push(row);
		this.state.selected = this.state.items.length - 1;
		if (!position) this.autoPlace(this.state.items.length - 1);
		else this.draw();
	}

	async autoPlace(index) {
		const row = this.state.items[index];
		const others = this.state.items.filter((_, i) => i !== index);
		const spot = await frappe.xcall("ioi_retail_suite.api.suggest_position", {
			items: JSON.stringify(others),
			width_cm: this.state.width,
			depth_cm: this.state.depth,
			item: row.item,
		});
		Object.assign(row, { x_cm: spot.x_cm, y_cm: spot.y_cm });
		if (!spot.fits) frappe.show_alert({ message: "Aucune place libre trouvée", indicator: "orange" });
		this.draw();
	}

	selectItem(id) {
		const index = Number(String(id).replace("box-", ""));
		if (!Number.isNaN(index)) {
			this.state.selected = index;
			this.draw();
		}
	}

	rotateSelected(step = 90) {
		const row = this.state.items[this.state.selected];
		if (!row) return;
		row.rot_z = (((Number(row.rot_z || 0) + step) % 360) + 360) % 360;
		row.rotation = String(Math.round(row.rot_z / 90) % 4 * 90);
		this.draw();
	}

	/** Write one or more fields onto the selected piece and redraw. */
	patchSelected(patch) {
		const row = this.state.items[this.state.selected];
		if (!row) return;
		Object.assign(row, patch);
		if (patch.rot_z !== undefined) {
			row.rotation = String(Math.round(Number(row.rot_z || 0) / 90) % 4 * 90);
		}
		this.draw();
	}

	/** Live translation while an object is dragged in the 3D view. */
	nudgeById(id, delta) {
		const index = Number(String(id).replace("box-", ""));
		const row = this.state.items[index];
		if (!row) return;
		const g = this.options.grid;
		row.x_cm = Math.max(0, Math.round((row.x_cm + delta.dx) / g) * g);
		row.y_cm = Math.max(0, Math.round((row.y_cm + delta.dy) / g) * g);
		row.z_cm = Math.max(0, Math.round(((row.z_cm || 0) + (delta.dz || 0)) / g) * g);
		this.state.selected = index;
		this.draw3d();
		this.renderInspector();
	}

	/** End of a 3D drag : the metrics and the 2D plan catch up once. */
	commitMove() {
		this.draw();
	}

	/**
	 * Material picker, opened by a right click on a piece in the 3D view.
	 *
	 * The choice is stored on the plan line, so it survives a save and reaches
	 * the quote PDF unchanged.
	 */
	openMaterialPicker(box, at) {
		const index = Number(String(box.parent || box.id).replace("box-", ""));
		const row = this.state.items[index];
		if (!row || !this.state.materials) return;
		this.closeMaterialPicker();
		const route = this.state.materials.route;
		const entries = Object.entries(this.state.materials.materials || {});
		const pop = document.createElement("div");
		pop.className = "rp-matpop";
		pop.innerHTML =
			`<div class="rp-matpop-h">Matière — ${frappe.utils.escape_html(row.label || row.item)}</div>` +
			`<div class="rp-matgrid">` +
			`<button data-mat="" class="rp-matchip${!row.material ? " on" : ""}">
				<i style="background:linear-gradient(135deg,#e9edf2,#c9d0d8)"></i><span>Par défaut</span></button>` +
			entries
				.map(
					([key, m]) => `<button data-mat="${key}" class="rp-matchip${row.material === key ? " on" : ""}">
						<i style="background-image:url(${route}${key}.jpg)"></i>
						<span>${frappe.utils.escape_html(m.label)}</span></button>`
				)
				.join("") +
			`</div>`;
		document.body.appendChild(pop);
		const w = pop.offsetWidth || 250;
		pop.style.left = `${Math.max(8, Math.min(window.innerWidth - w - 8, at.x - w / 2))}px`;
		pop.style.top = `${Math.max(8, at.y + 10)}px`;
		pop.querySelectorAll("[data-mat]").forEach((node) => {
			node.onclick = () => {
				row.material = node.dataset.mat || null;
				this.closeMaterialPicker();
				this.state.selected = index;
				this.draw();
			};
		});
		this._matpop = pop;
		this._matAway = (event) => {
			if (!pop.contains(event.target)) this.closeMaterialPicker();
		};
		setTimeout(() => document.addEventListener("pointerdown", this._matAway), 0);
	}

	closeMaterialPicker() {
		if (this._matAway) document.removeEventListener("pointerdown", this._matAway);
		if (this._matpop && this._matpop.parentNode) this._matpop.parentNode.removeChild(this._matpop);
		this._matpop = null;
		this._matAway = null;
	}

	removeSelected() {
		if (this.state.selected == null) return;
		this.state.items.splice(this.state.selected, 1);
		this.state.selected = null;
		this.draw();
	}

	async refreshMetrics() {
		const metrics = await frappe.xcall("ioi_retail_suite.api.analyse_plan", {
			items: JSON.stringify(this.state.items),
			width_cm: this.state.width,
			depth_cm: this.state.depth,
		});
		this.state.metrics = metrics;
		this.el.metrics.innerHTML = `
			<div><span>Surface</span><b>${metrics.room_area_m2} m²</b></div>
			<div><span>Occupée</span><b>${metrics.occupied_area_m2} m²</b></div>
			<div><span>Circulation</span><b>${metrics.circulation_pct} %</b></div>
			<div><span>Valeur</span><b>${format_currency(metrics.value)}</b></div>`;
		this.el.warnings.innerHTML = (metrics.warnings || [])
			.map((w) => `<div class="rp-warn rp-${w.severity.toLowerCase()}">${frappe.utils.escape_html(w.text)}</div>`)
			.join("");
		if (this.options.onChange) this.options.onChange(this.state);
	}

	async save() {
		const result = await frappe.xcall("ioi_retail_suite.api.save_plan", {
			title: this.el.title.value || "Plan",
			items: JSON.stringify(this.state.items),
			width_cm: this.state.width,
			depth_cm: this.state.depth,
			customer: this.el.customer.value || null,
			plan: this.state.plan,
			wall_height_cm: this.state.height,
		});
		this.state.plan = result.plan;
		frappe.show_alert({ message: `Plan ${result.plan} enregistré`, indicator: "green" });
		return result;
	}

	/**
	 * A quote needs a customer.
	 *
	 * ioi refuses to number a Sales Quote without one — VAT regime, payment
	 * term and sales agent are all read off the customer record. Rather than
	 * letting the server throw halfway through, ask for it here.
	 */
	async ensureCustomer() {
		if (this.el.customer.value) return this.el.customer.value;
		return new Promise((resolve) => {
			const dialog = new frappe.ui.Dialog({
				title: __("Client du devis"),
				fields: [
					{
						fieldtype: "HTML",
						options:
							`<p style="color:var(--rs-mut);font-size:12.5px;margin:0 0 4px">
							 Un devis ioi porte le régime de TVA, la condition de paiement et le
							 commercial du client : il ne peut pas être émis sans lui.</p>`,
					},
					{
						fieldtype: "Link",
						fieldname: "customer",
						label: __("Client"),
						options: "ioi Customer",
						reqd: 1,
					},
				],
				primary_action_label: __("Continuer"),
				primary_action: (values) => {
					dialog.hide();
					this.setCustomer(values.customer);
					resolve(values.customer);
				},
			});
			dialog.onhide = () => resolve(this.el.customer.value || null);
			dialog.show();
		});
	}

	/** Select a customer, adding it to the list when it is not among the 200 loaded. */
	setCustomer(name) {
		if (!name) return;
		const has = Array.from(this.el.customer.options).some((o) => o.value === name);
		if (!has) {
			const option = document.createElement("option");
			option.value = name;
			option.textContent = name;
			this.el.customer.appendChild(option);
		}
		this.el.customer.value = name;
	}

	async toQuote() {
		const customer = await this.ensureCustomer();
		if (!customer) {
			frappe.show_alert({ message: __("Devis annulé : aucun client."), indicator: "orange" });
			return null;
		}
		// the select is populated from the model, so its first entry is a valid
		// journal even when the user never touched it
		const journal = this.el.journal.value || (this.el.journal.options[0] || {}).value;
		if (!journal) {
			frappe.msgprint({
				title: __("Journal de vente manquant"),
				indicator: "red",
				message: __("Aucun journal n'est rattaché au modèle dans Retail Settings."),
			});
			return null;
		}
		if (!this.state.model) {
			frappe.msgprint({
				title: __("Aucun modèle publié"),
				indicator: "red",
				message: __("Publiez un Retail Configurator Model avant de générer un devis."),
			});
			return null;
		}
		if (!this.state.items.length) {
			frappe.msgprint(__("Le plan est vide."));
			return null;
		}

		const button = this.root.querySelector("#rp-quote");
		button.disabled = true;
		button.textContent = __("Génération…");
		try {
			if (!this.state.plan) await this.save();
			const configuration = await frappe.xcall(
				"ioi_retail_suite.api.plan_to_configuration",
				{
					plan: this.state.plan,
					model: this.state.model,
					customer: customer,
					journal: journal,
				}
			);
			const created = await frappe.xcall("ioi_retail_suite.api.create_document", {
				configuration: configuration.configuration,
				kind: "quote",
			});
			frappe.msgprint({
				title: __("Devis créé"),
				indicator: "green",
				message:
					`<a href="${created.route}" target="_blank"><b>${created.name}</b></a> — ` +
					`${format_currency(created.total_tvac)} TVAC, depuis le plan ` +
					`<b>${frappe.utils.escape_html(this.state.plan)}</b>.`,
			});
			return created;
		} catch (error) {
			// xcall already shows the server message ; say which step gave up
			frappe.show_alert({
				message: __("Le devis n'a pas pu être généré."),
				indicator: "red",
			});
			console.error("toQuote", error);
			return null;
		} finally {
			button.disabled = false;
			button.textContent = __("Générer le devis ioi");
		}
	}

	_bind() {
		this.root.querySelector("#rp-view2d").onclick = () => {
			this.state.view = "2d";
			this.draw();
		};
		this.root.querySelector("#rp-view3d").onclick = () => {
			this.state.view = "3d";
			this.viewer.setMode("iso");
			this.draw();
		};
		this.root.querySelector("#rp-orbit").onclick = () => {
			this.state.view = "3d";
			this.viewer.setMode("orbit");
			this.draw();
		};
		this.root.querySelector("#rp-eyeview").onclick = () => {
			this.state.view = "3d";
			this.viewer.setEyeHeight(Number(this.el.eye.value) || 175);
			this.viewer.setMode("eye");
			this.draw();
			// walking needs the keyboard, so hand the stage the focus
			this.el.stage3d.focus();
		};
		this.el.eye.onchange = () => {
			this.state.eyeHeight = Number(this.el.eye.value) || 175;
			this.viewer.setEyeHeight(this.state.eyeHeight);
		};
		this.root.querySelector("#rp-full").onclick = () => {
			// full screen only makes sense on the 3D stage, so switch to it first
			if (this.state.view !== "3d") {
				this.state.view = "3d";
				this.draw();
			}
			this.viewer.toggleFullscreen();
		};
		this.root.querySelector("#rp-rotate-cam").onclick = () => this.viewer.rotate(1);
		this.root.querySelector("#rp-rotate").onclick = () => this.rotateSelected(90);
		this.root.querySelector("#rp-delete").onclick = () => this.removeSelected();
		this.el.open.onclick = () => this.openPlanDialog();
		this.root.querySelector("#rp-save").onclick = () => this.save();
		this.root.querySelector("#rp-quote").onclick = () => this.toQuote();
		this.el.width.onchange = () => {
			this.state.width = Number(this.el.width.value) || 400;
			this.draw();
		};
		this.el.depth.onchange = () => {
			this.state.depth = Number(this.el.depth.value) || 300;
			this.draw();
		};
		this.el.search.oninput = frappe.utils.debounce(
			() => this.loadCatalog(this.el.search.value),
			300
		);
		window.addEventListener("resize", frappe.utils.debounce(() => this.draw(), 200));
	}
}

/** {material: centimetres per texture tile}, from api.material_catalog. */
function tileTable(catalogue) {
	const out = {};
	for (const [key, spec] of Object.entries((catalogue && catalogue.materials) || {})) {
		if (spec.tile_cm) out[key] = spec.tile_cm;
	}
	return Object.keys(out).length ? out : null;
}

/** Z rotation in degrees : the free angle wins, the old Select is the fallback. */
function angleOf(row) {
	const free = Number(row.rot_z);
	if (free) return free;
	return Number(row.rotation || 0) || 0;
}

/**
 * Axis-aligned bounding box of a turned footprint.
 *
 * Mirrors `transform.footprint_aabb` in Python : the plan, the collisions and
 * the metré all reason on axis-aligned rectangles, and a box that only ever
 * grows can never hide an overlap.
 */
function footprint(row) {
	const w = Number(row.width_cm) || 80;
	const d = Number(row.depth_cm) || 60;
	const a = (angleOf(row) * Math.PI) / 180;
	const c = Math.abs(Math.cos(a));
	const s = Math.abs(Math.sin(a));
	return [w * c + d * s, w * s + d * c];
}

/** 0..1 opacity as the two hex digits appended to a #rrggbb colour. */
function alphaHex(value) {
	const a = Math.max(0, Math.min(1, value == null ? 0.1 : value));
	return Math.round(a * 255).toString(16).padStart(2, "0");
}

function format_currency(value) {
	return (
		Number(value || 0).toLocaleString("fr-BE", {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}) + " €"
	);
}

const TEMPLATE = `
<div class="rp">
	<aside class="rp-left">
		<div id="rp-objects" class="rp-objects"></div>
		<div class="rp-catwrap">
			<div class="rp-leftsplit">Catalogue</div>
			<input id="rp-search" class="rp-input" placeholder="Rechercher un article…">
			<div id="rp-families" class="rp-fams"></div>
			<div id="rp-catalog" class="rp-cat"></div>
		</div>
	</aside>
	<main class="rp-main">
		<div class="rp-bar">
			<button id="rp-open" class="rp-btn rp-primary">Ouvrir un plan…</button>
			<input id="rp-title" class="rp-input" value="Nouveau plan" style="max-width:180px">
			<label>L <input id="rp-width" class="rp-num" type="number" value="400" step="10"> cm</label>
			<label>P <input id="rp-depth" class="rp-num" type="number" value="300" step="10"> cm</label>
			<span class="rp-sep"></span>
			<button id="rp-view2d" class="rp-btn">Plan 2D</button>
			<button id="rp-view3d" class="rp-btn">Vue 3D</button>
			<button id="rp-orbit" class="rp-btn">Orbite</button>
			<button id="rp-eyeview" class="rp-btn">Hauteur d&#39;homme</button>
			<label>👁 <input id="rp-eye" class="rp-num" type="number" value="175" step="5"
				min="30" max="280"> cm</label>
			<button id="rp-rotate-cam" class="rp-btn">⟳ caméra</button>
			<button id="rp-full" class="rp-btn" title="Plein écran (F)">⛶</button>
			<span class="rp-sep"></span>
			<button id="rp-rotate" class="rp-btn">Pivoter 90°</button>
			<button id="rp-delete" class="rp-btn rp-danger">Supprimer</button>
		</div>
		<div id="rp-stage" class="rp-stage"></div>
		<div id="rp-stage3d" class="rp-stage" style="display:none"></div>
	</main>
	<aside class="rp-right">
		<div class="rp-rightscroll">
			<div id="rp-inspector" class="rp-inspector"></div>
			<div id="rp-metrics" class="rp-metrics"></div>
			<div id="rp-warnings" class="rp-warns"></div>
		</div>
		<div class="rp-rightfoot">
			<label class="rp-field"><span>Client <b class="rp-req">requis pour le devis</b></span>
				<select id="rp-customer"></select></label>
			<label class="rp-field"><span>Journal</span><select id="rp-journal"></select></label>
			<button id="rp-save" class="rp-btn rp-block">Enregistrer le plan</button>
			<button id="rp-quote" class="rp-btn rp-block rp-primary">Générer le devis ioi</button>
		</div>
	</aside>
</div>`;

if (typeof window !== "undefined") window.RetailRoomPlanner = RetailRoomPlanner;
